import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FolderOpen, Download, Certificate, FileText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

// Bilingual category labels — key by certificate_type when present,
// otherwise treat as a generic HR document.
const CERT_LABELS = {
  quality_patient_safety: { ar: 'شهادة الجودة وسلامة المرضى', en: 'Quality & Patient Safety Certificate' },
  safety_fire_prevention: { ar: 'شهادة السلامة والوقاية من الحرائق', en: 'Safety Orientation & Fire Prevention Certificate' },
  infection_control:      { ar: 'شهادة مكافحة العدوى', en: 'Infection Control Orientation Certificate' },
};

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function EmployeeMyDocuments() {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/my/documents`, { withCredentials: true });
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(isAr ? 'تعذّر جلب المستندات' : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const res = await axios.get(`${API}/my/documents/${doc.id}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      // v1.5.54 — Force a reliable download with the correct .pdf
      // filename on every browser (mobile Safari would previously save
      // as a blob without extension). Explicitly wrap in a Blob with
      // the right MIME so the browser writes the file with the
      // extension carried by `download=`.
      const filename = doc.original_name || doc.filename || 'document.pdf';
      const inferredType = filename.toLowerCase().endsWith('.pdf')
        ? 'application/pdf'
        : (res.data.type || 'application/octet-stream');
      const blob = new Blob([res.data], { type: inferredType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(isAr ? 'تعذّر تحميل المستند' : 'Failed to download document');
    } finally {
      setDownloadingId(null);
    }
  };

  const labelFor = (doc) => {
    if (doc.certificate_type && CERT_LABELS[doc.certificate_type]) {
      const base = CERT_LABELS[doc.certificate_type][language] || CERT_LABELS[doc.certificate_type].en;
      return doc.certificate_grant_date ? `${base} — ${doc.certificate_grant_date}` : base;
    }
    return doc.label || doc.original_name || doc.filename;
  };

  const categoryBadge = (doc) => {
    if (doc.certificate_type) {
      return (
        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 gap-1">
          <Certificate size={12} weight="duotone" />
          {isAr ? 'شهادة تدريب' : 'Training'}
        </Badge>
      );
    }
    return (
      <Badge className="bg-[#E8E6F5] text-[#655dc6] border border-[#C4BFF0] gap-1">
        <FileText size={12} weight="duotone" />
        {isAr ? 'مستند' : 'Document'}
      </Badge>
    );
  };

  const formatUploadedAt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      // v1.5.55 — Always render the issue date in the Gregorian calendar,
      // even when the UI is in Arabic. Some browsers default `ar-SA` to
      // Umm-al-Qura (Hijri) which is confusing for HR records that
      // reference Gregorian grant dates.
      return d.toLocaleDateString(isAr ? 'ar' : 'en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        calendar: 'gregory',
        numberingSystem: 'latn',
      });
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="employee-my-documents-page" dir={direction}>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-[#655dc6]" data-testid="my-documents-heading">
            {isAr ? 'مستنداتي' : 'My Documents'}
          </h1>
          <p className="text-sm text-[#655dc6]/70 mt-1">
            {isAr
              ? 'شهادات التدريب والمستندات التي أصدرتها لك إدارة الموارد البشرية.'
              : 'Training certificates and documents issued to you by HR.'}
          </p>
        </div>

        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
              <FolderOpen size={22} weight="duotone" className="text-[#655dc6]" />
              {isAr ? `المستندات (${docs.length})` : `Documents (${docs.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32" data-testid="my-documents-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : docs.length === 0 ? (
              <div className="text-center py-12 text-[#655dc6]/70" data-testid="my-documents-empty">
                <FolderOpen size={44} weight="duotone" className="mx-auto mb-3 opacity-50" />
                <p>{isAr ? 'لا توجد مستندات بعد.' : 'No documents yet.'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto scrollbar-thin" data-mobile-hint="scroll-x">
                <Table data-testid="my-documents-table">
                  <TableHeader>
                    <TableRow className="border-[#C4BFF0]">
                      <TableHead className="text-start">{isAr ? 'المستند' : 'Document'}</TableHead>
                      <TableHead className="text-start hidden sm:table-cell">{isAr ? 'النوع' : 'Type'}</TableHead>
                      <TableHead className="text-start">{isAr ? 'تاريخ الإصدار' : 'Issued'}</TableHead>
                      <TableHead className="text-start hidden md:table-cell">{isAr ? 'الحجم' : 'Size'}</TableHead>
                      <TableHead className="text-start">{isAr ? 'تحميل' : 'Download'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {docs.map((doc) => (
                      <TableRow key={doc.id} className="border-[#C4BFF0]" data-testid={`my-doc-row-${doc.id}`}>
                        <TableCell className="font-medium text-[#655dc6] max-w-md">
                          <div>{labelFor(doc)}</div>
                          {/* On phones the Type badge lives under the title
                              so we can drop its dedicated column. */}
                          <div className="sm:hidden mt-1">{categoryBadge(doc)}</div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{categoryBadge(doc)}</TableCell>
                        <TableCell className="text-[#655dc6]/70 whitespace-nowrap">{formatUploadedAt(doc.uploaded_at)}</TableCell>
                        <TableCell className="text-[#655dc6]/70 hidden md:table-cell">{formatSize(doc.size)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(doc)}
                            disabled={downloadingId === doc.id}
                            className="text-[#655dc6] hover:bg-[#E8E6F5] gap-1"
                            data-testid={`my-doc-download-${doc.id}`}
                          >
                            {downloadingId === doc.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#655dc6]" />
                            ) : (
                              <>
                                <Download size={16} />
                                <span className="hidden sm:inline">{isAr ? 'تحميل' : 'Download'}</span>
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
